import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { SYSTEMSTATUS_PATH, ZURUECKSETZEN_PATH } from '@mcikm-vote/shared';
import type { SystemstatusAntwort, ZuruecksetzenErgebnis } from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';

/**
 * Anbindung des Systemstatus (§20.4, §32).
 *
 * Die Werte werden serverseitig bei jedem Aufruf frisch erhoben. Ein Takt hält sie aktuell, ohne
 * dass jemand neu laden muss — Speicherplatz und Verbindungszahl ändern sich im Betrieb laufend.
 */

export const SYSTEMSTATUS_SCHLUESSEL = ['admin', 'systemstatus'] as const;

/** Abstand der Statusabfrage. Bewusst träge: Der Stand ändert sich langsam und kostet `stat`-Aufrufe. */
export const SYSTEMSTATUS_ABSTAND_MS = 30_000;

export function useSystemstatus(): UseQueryResult<SystemstatusAntwort, ApiFehler> {
  return useQuery<SystemstatusAntwort, ApiFehler>({
    queryKey: SYSTEMSTATUS_SCHLUESSEL,
    queryFn: () => apiAnfrage<SystemstatusAntwort>(SYSTEMSTATUS_PATH),
    refetchInterval: SYSTEMSTATUS_ABSTAND_MS,
  });
}

/**
 * Setzt das System vollständig zurück (ÄA-3).
 *
 * Entwertet danach den **gesamten** Zwischenspeicher, nicht nur den Systemstatus: Veranstaltungen,
 * Abstimmungen, Tokens, Exporte, Backups und das Protokoll sind allesamt hinfällig. Eine gezielte
 * Liste von Schlüsseln wäre hier die schlechtere Wahl — sie müsste bei jeder neuen Ansicht
 * nachgepflegt werden, und die vergessene zeigte weiter Daten, die es nicht mehr gibt.
 */
export function useSystemZuruecksetzen(): UseMutationResult<
  ZuruecksetzenErgebnis,
  ApiFehler,
  string
> {
  const queryClient = useQueryClient();

  return useMutation<ZuruecksetzenErgebnis, ApiFehler, string>({
    mutationFn: (schluessel) =>
      apiAnfrage<ZuruecksetzenErgebnis>(ZURUECKSETZEN_PATH, {
        methode: 'POST',
        koerper: { schluessel },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}
